import type { ChangeEvent } from 'react';
import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { find } from 'lodash';
import cn from 'classnames';
import { RadioButton, Button } from '@lt/components';
import LoaderIcon from '@lt/components/icons-react/rebrand/Loader';
import { Cashback as CashBackIcon } from '@lt/components/icons-react/userflow';

import { i18n } from '@lt/localization';
import type { DiscountMethod } from 'checkout/types/payment';
import { formatNumber } from '../../../../../utils/stringUtils';
import { DiscountError } from '../DiscountError';
import AppliedCashbackOrPromo from './AppliedCashbackOrPromo';

import type { DISCOUNT_TYPES } from '../../../store/constants/discount';
import {
  DISCOUNT_COUPON,
  DISCOUNT_BONUS,
  DISCOUNT_GIFT_CERT,
} from '../../../store/constants/discount';

import GiftCertificateInfoContainer from '../GiftCertificate';
import CashbackNotice from './CashbackNotice';

const baseCls = 'checkout-discount';

const RADIOGROUP_NAME = 'discount';

interface DiscountProps {
  addCoupon: (value: string | null) => void;
  addBonus: (value: string | null) => void;
  appliedType?: string;
  bonusScore: number;
  discountMethods?: DiscountMethod[];
  discountPrice?: number;
  fullPrice?: number;
  errors: {
    baseErrors?: string[];
    couponErrors?: string[];
  };
  isFetching?: boolean;
  isMobile?: boolean;
  isHotelOnly?: boolean;
  isWhitelabel: boolean;
}

interface DiscountState {
  discountType: string;
  value: string;
  isFetching: boolean;
}

class Discount extends PureComponent<DiscountProps, DiscountState> {
  static getDerivedStateFromProps(nextProps: DiscountProps) {
    const { isFetching } = nextProps;
    if (!nextProps.isFetching) {
      return { isFetching };
    }
    return null;
  }

  static getOptionTitle(discount) {
    if (discount.type === DISCOUNT_BONUS && !discount.enable) {
      return i18n.t('checkout.discount.noBonusTitle');
    }

    return discount.label;
  }

  constructor(props) {
    super(props);

    const { availableMethods } = this;
    const { bonusScore } = this.props;
    try {
      const defaultTargetType = bonusScore ? DISCOUNT_BONUS : DISCOUNT_COUPON;

      const defaultMethod =
        find(availableMethods, (item) => item.type === defaultTargetType) ||
        availableMethods[0];

      this.state = {
        discountType: defaultMethod.type,
        value: '',
        isFetching: false,
      };
    } catch (e) {
      console.error(e);
      this.state = {
        discountType: availableMethods[0].type,
        value: '',
        isFetching: false,
      };
    }

    this.handleBonusChange = this.handleBonusChange.bind(this);
    this.handleSetDiscount = this.handleSetDiscount.bind(this);
  }

  handleBonusChange(e: ChangeEvent<HTMLInputElement>) {
    const { value: currentValue } = e.target;
    const value = Number(currentValue) > 0 ? currentValue : '';
    this.setState({ value });
  }

  handleSetDiscount(
    value: string | null,
    newDiscountType?: keyof typeof DISCOUNT_TYPES,
  ) {
    this.setState({ isFetching: true });
    if (value === null) {
      // cancelled
      if (newDiscountType) {
        this.setState({ discountType: newDiscountType, value: '' });
      } else {
        this.setState({ value: '' });
      }
    }
    const { discountType } = this.state;
    const { addCoupon, addBonus } = this.props;
    const addDiscount = discountType === DISCOUNT_BONUS ? addBonus : addCoupon;
    addDiscount(value ? value.toString().trim() : null);
  }

  private setValue = (e: ChangeEvent<HTMLInputElement>) =>
    this.setState({ value: e.target.value });

  getMethodByType(type: string) {
    const { discountMethods = [] } = this.props;
    return find(discountMethods, (item) => item.type === type);
  }

  get availableMethods() {
    const { discountMethods = [] } = this.props;
    return discountMethods.filter((item) => item.enable);
  }

  get isMultipleOptions() {
    const { discountMethods = [] } = this.props;
    return discountMethods.length > 1;
  }

  get title() {
    if (this.isMultipleOptions) {
      return i18n.t('checkout.discount.title');
    }
    const [discount] = this.availableMethods;
    return discount && discount.label;
  }

  renderRadio({ type, enable }: DiscountMethod) {
    const { discountType } = this.state;
    return (
      <div className={`${baseCls}__radio`}>
        <RadioButton
          name={RADIOGROUP_NAME}
          id={type}
          className="checkout-radiobutton"
          value={type}
          checked={discountType === type}
          disabled={!enable}
          onChange={() => this.setState({ discountType: type, value: '' })}
        />
      </div>
    );
  }

  renderOptionLabel(discountMethod) {
    const showRadio = this.isMultipleOptions;

    const content = (
      <h3
        className={cn(`${baseCls}__subtitle`, {
          [`${baseCls}__subtitle__disable`]: !discountMethod.enable,
        })}
      >
        {Discount.getOptionTitle(discountMethod)}
      </h3>
    );
    return (
      <div className="flex">
        {showRadio ? (
          <label htmlFor={discountMethod.type}>{content}</label>
        ) : (
          content
        )}
      </div>
    );
  }

  renderCouponError() {
    const {
      errors: { couponErrors },
    } = this.props;

    if (!couponErrors || !couponErrors.length) {
      return null;
    }

    return <DiscountError errors={couponErrors} />;
  }

  renderSetDiscountBtn() {
    const { value, discountType, isFetching } = this.state;
    const {
      appliedType,
      isMobile,
      isFetching: isFetchingFromProps,
    } = this.props;
    const loading = isFetching || isFetchingFromProps;

    const btnText =
      (appliedType === DISCOUNT_BONUS &&
        i18n.t('checkout.discount.cancelBonus')) ||
      (appliedType === DISCOUNT_COUPON &&
        i18n.t('checkout.discount.cancelCoupon')) ||
      (discountType === DISCOUNT_BONUS
        ? i18n.t('checkout.discount.spend')
        : i18n.t('checkout.discount.activate'));

    return (
      <DiscountButton
        primary
        type="button"
        disabled={!(value || appliedType)}
        isMobile={isMobile}
        isCashback={discountType === DISCOUNT_BONUS}
        isCancel={appliedType}
        onClick={() => this.handleSetDiscount(appliedType ? null : value)}
      >
        {loading && !appliedType ? <StyledLoaderIcon /> : btnText}
      </DiscountButton>
    );
  }

  renderBonus() {
    const discountMethod = this.getMethodByType(DISCOUNT_BONUS);
    const { isWhitelabel } = this.props;
    if (!discountMethod || isWhitelabel) {
      return null;
    }
    const { value, discountType } = this.state;
    const {
      bonusScore,
      isHotelOnly,
      isMobile,
      errors: { baseErrors },
    } = this.props;
    const showRadio = this.isMultipleOptions;

    const isSelected = discountMethod.type === discountType;

    const discountInfo = isHotelOnly
      ? i18n.t('checkout.discount.hotelBonus')
      : i18n.t('checkout.discount.tourBonus');

    return (
      <div className={`${baseCls}__row`}>
        {showRadio && this.renderRadio(discountMethod)}
        <div className={`${baseCls}__block`}>
          {this.renderOptionLabel(discountMethod)}
          <div className="flex">
            <p
              className={cn(`${baseCls}__text`, {
                [`${baseCls}__text__disable`]: !discountMethod.enable,
              })}
            >
              {bonusScore ? discountInfo : i18n.t('checkout.discount.noBonus')}
            </p>
          </div>
          {isSelected && (
            <>
              <CashbackHintBlock isMobile={isMobile}>
                <CashbackCount>1</CashbackCount>
                <CashBackIcon width={24} height={24} />
                <span>{i18n.t('checkout.discount.bonusFormula')}</span>
              </CashbackHintBlock>
              <CashbackApplyForm>
                <CashbackInputContainer isMobile={isMobile}>
                  <StyledInput
                    onChange={this.handleBonusChange}
                    value={value}
                    type="number"
                    min="0"
                    placeholder={`${i18n.t('checkout.discount.paymentInput')}?`}
                    isMobile={isMobile}
                    hasError={baseErrors && baseErrors?.length > 0}
                  />
                  <CashbackRemainingCount>
                    {i18n.t('checkout.discount.availableAmount', {
                      amount: formatNumber(bonusScore),
                    })}
                  </CashbackRemainingCount>
                </CashbackInputContainer>
                {this.renderSetDiscountBtn()}
              </CashbackApplyForm>
              {baseErrors?.length && (
                <DiscountError
                  errors={baseErrors.map((it) => {
                    if (it === 'loyalty_not_available') {
                      return i18n.t('checkout.discount.loyaltyNotAvailable');
                    }

                    if (it === 'balance_changed') {
                      return i18n.t('checkout.discount.balanceChanged', {
                        bonus: bonusScore,
                      });
                    }

                    return it;
                  })}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  renderPromocode() {
    const discountMethod = this.getMethodByType(DISCOUNT_COUPON);
    if (!discountMethod) {
      return null;
    }
    const { value, discountType } = this.state;
    const {
      errors: { couponErrors },
      isMobile,
    } = this.props;

    const showRadio = this.isMultipleOptions;
    const isSelected = discountMethod.type === discountType;
    return (
      <div className={`${baseCls}__row`}>
        {showRadio && this.renderRadio(discountMethod)}
        <div className={`${baseCls}__block`}>
          {this.renderOptionLabel(discountMethod)}
          <div className="flex">
            <p className={`${baseCls}__text`}>
              {i18n.t('checkout.discount.promocode')}
            </p>
          </div>

          {isSelected && (
            <PromoForm isMobile={isMobile}>
              <StyledPromoInputContainer isMobile={isMobile}>
                <StyledPromoInput
                  placeholder="LT-0000-0000-0000"
                  onChange={this.setValue}
                  value={value}
                  isMobile={isMobile}
                  hasError={couponErrors && couponErrors?.length > 0}
                />
                {isSelected && this.renderCouponError()}
              </StyledPromoInputContainer>
              {this.renderSetDiscountBtn()}
            </PromoForm>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { appliedType, discountPrice, fullPrice, isMobile } = this.props;

    return (
      <div>
        <div className="flex">
          <h2 className={`${baseCls}__title like-h2`}>{this.title}</h2>
        </div>
        <div className={`${baseCls}__wrapper`}>
          {!appliedType && (
            <div className={baseCls}>
              <CashbackNotice />
              {this.renderBonus()}
              {this.renderPromocode()}
            </div>
          )}
          {appliedType && (
            <div className={`${baseCls}__overlay`}>
              {appliedType === DISCOUNT_GIFT_CERT ? (
                <GiftCertificateInfoContainer />
              ) : (
                <AppliedCashbackOrPromo
                  appliedType={appliedType}
                  discountPrice={discountPrice}
                  fullPrice={fullPrice}
                  isMobile={isMobile}
                  setDiscountButton={this.renderSetDiscountBtn()}
                  onCancelBonus={() =>
                    this.handleSetDiscount(null, DISCOUNT_COUPON)
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

const StyledLoaderIcon = styled(LoaderIcon)`
  height: 30px;
  animation: var(--loader-animation);
  path {
    fill: var(--white);
  }
`;

const CashbackCount = styled.span`
  color: #5d6fb4;
  font-size: 12px;
`;

const CashbackApplyForm = styled.div`
  display: flex;
  align-items: flex-start;
`;

const CashbackInputContainer = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-right: 16px;
  flex: ${({ isMobile }) => (isMobile ? 2 : 0)} 0 140px;
`;

const StyledInput = styled.input<{ isMobile?: boolean; hasError?: boolean }>`
  padding: ${({ isMobile }) => (isMobile ? 14 : 12)}px 8px;
  box-sizing: border-box;
  height: ${({ isMobile }) => (isMobile ? 43 : 40)}px;
  width: 230px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.blackBase};
  border: 1px solid ${({ hasError }) => (hasError ? '#D0021B' : '#C2C5CC')};
  border-radius: 4px;
  background-color: ${({ theme, hasError }) =>
    hasError ? theme.colors.pinkBase : 'transparent'};

  &[type='number']::-webkit-outer-spin-button,
  &[type='number']::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }

  &::-webkit-input-placeholder {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.greyMedium};
  }

  &::-moz-placeholder {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.greyMedium};
  }

  &:-ms-input-placeholder {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.greyMedium};
  }

  &:-moz-placeholder {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.greyMedium};
  }
`;

const StyledPromoInput = styled(StyledInput)<{ isMobile?: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '215px')};
  ${({ isMobile }) => (!isMobile ? 'margin-right: 16px' : '')};
`;

const CashbackRemainingCount = styled.span`
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.greyLight};
`;

const DiscountButton = styled(Button)<{
  isMobile?: boolean;
  isCashback?: boolean;
  isCancel?: boolean;
}>`
  min-height: ${({ isMobile }) => (isMobile ? 43 : 40)}px;
  max-height: ${({ isMobile }) => (isMobile ? 43 : 40)}px;
  width: ${({ isMobile }) => (isMobile ? '100%' : 'max-content')};
  ${({ isMobile, isCancel }) =>
    !isMobile && isCancel ? 'min-width: 185px' : ''};
  flex: ${({ isMobile }) => (isMobile ? '1 1 140px' : '0 0 140px')};
  ${({ isCashback, isMobile }) =>
    !isCashback && isMobile ? 'margin-top: 16px' : ''};
  font-size: 14px;
  border-radius: var(--border-radius);

  &--edit {
    width: auto;
  }

  &:hover div::before {
    border-color: #179fe3;
    border-left-color: rgba(255, 255, 255, 0.3);
    border-bottom-color: rgba(255, 255, 255, 0.3);
  }

  & div::before {
    margin-top: 2px;
    border-color: #007aff;
    border-left-color: rgba(255, 255, 255, 0.3);
    border-bottom-color: rgba(255, 255, 255, 0.3);
  }
`;

const PromoForm = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  margin-top: 12px;
`;

const StyledPromoInputContainer = styled.div<{ isMobile?: boolean }>`
  ${({ isMobile }) => (isMobile ? 'flex-grow: 1' : '')};
`;

const CashbackHintBlock = styled.div<{ isMobile?: boolean }>`
  color: #919499;
  font-size: ${({ isMobile }) => (isMobile ? '12px' : '14px')};
  line-height: 16px;
  display: flex;
  align-items: center;
  gap: 3px;
  margin: ${({ isMobile }) => (isMobile ? '5px 0 11px 0' : '7px 0 13px 0')};
`;

export default Discount;
